import { motion } from "framer-motion";
import { UserMinus, Hourglass, Briefcase } from "lucide-react";

const PAIN_POINTS = [
  {
    icon: UserMinus,
    title: "Your leads go cold",
    body: "40% of quotes never get a follow-up. An AI agent follows up in minutes, not days.",
    stat: "40%",
    statLabel: "of quotes lost",
  },
  {
    icon: Hourglass,
    title: "Your content is slow and expensive",
    body: "Traditional production takes weeks and costs tens of thousands. AI-native workflows deliver in days.",
    stat: "10x",
    statLabel: "faster delivery",
  },
  {
    icon: Briefcase,
    title: "Your team is drowning in manual work",
    body: "Every hour spent on repetitive tasks is an hour not spent growing your business.",
    stat: "24/7",
    statLabel: "always on",
  },
];

export default function Problem() {
  return (
    <section
      data-testid="problem-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.22em] text-[#00d4ff]/90" data-testid="problem-eyebrow">
            The Problem
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance"
            data-testid="problem-headline"
          >
            You&apos;re Leaving Money on the Table
          </motion.h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-xl">
            Most businesses lose revenue silently — in slow follow-ups,
            expensive production, and time spent on tasks software should
            handle.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {PAIN_POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                data-testid={`problem-card-${i}`}
                className="group relative flex flex-col rounded-xl border border-white/10 bg-[#13131a]/80 p-7 hover:border-[#00d4ff]/30 hover:bg-[#16161f] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#00d4ff]/30 bg-[#00d4ff]/10">
                    <Icon className="h-5 w-5 text-[#00d4ff]" />
                  </span>
                  <div className="text-right">
                    <div className="font-display text-2xl font-semibold text-white">
                      {p.stat}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                      {p.statLabel}
                    </div>
                  </div>
                </div>
                <h3 className="font-display mt-6 text-xl font-semibold text-white">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-zinc-400">
                  {p.body}
                </p>

                <div className="pointer-events-none absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_top,rgba(0,212,255,0.08),transparent_60%)]" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
